// UnknownAlumniRegistration.jsx
import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  Grid,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  useTheme,
  Fade,
  Snackbar,
  Alert,
  Button,
  Stack,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import PageLayout from 'examples/LayoutContainers/PageLayout';
import api from '../../../API/axiosInstance';
import { API_ENDPOINTS } from '../../../config/api';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import AddIcon from '@mui/icons-material/Add';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';

const OTHER_OPTION = '__OTHER__';
const LOCAL_OPTIONS_KEY = 'unknownAlumniCustomDropdownOptions';
const GREEN = '#2e7d32';
const GREEN_SOFT = 'rgba(46, 125, 50, 0.08)';

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear + 5 - 1979 }, (_, i) => String(1980 + i));

const DEFAULT_OPTIONS = {
  gender: ['Male', 'Female'],
  category: ['U.G.', 'P.G.', 'M.Phil', 'Ph.D.'],
  courseStudied: ['B.E', 'B.Tech', 'M.E', 'M.Tech', 'MBA', 'MCA', 'Ph.D'],
  branch: [
    'AIDS',
    'Bio Tech',
    'CSE',
    'IT',
    'ECE',
    'EEE',
    'Mechanical',
    'Civil',
    'Food Technology',
    'Nano Science and Technology',
    'Textile Technology',
    'Chemical Engineering',
    'Automobile Engineering',
  ],
  presentStatus: [
    'Working',
    'Student',
    'Higher Education',
    'Business',
    'Freelancing',
    'Looking for Job',
  ],
  availability: ['India', 'Abroad', 'Available'],
  domain: ['Embedded', 'Web Application', 'IoT', 'Data Science', 'Cloud', 'Cyber Security', 'AI/ML'],
  organization: [],
  designation: ['Engineer', 'Senior Engineer', 'Analyst', 'Manager', 'Consultant', 'Developer'],
  location: ['Chennai', 'Coimbatore', 'Bengaluru', 'Salem', 'Hosur', 'Tiruchengode'],
};

const DROPDOWN_FIELDS = Object.keys(DEFAULT_OPTIONS);

const initialForm = {
  fullName: '',
  email: '',
  gender: '',
  dateOfBirth: '',
  rollNumber: '',
  batchFrom: '',
  batchTo: '',
  batch: '',
  collegeStudied: 'KSRCT',
  courseStudied: '',
  branch: '',
  category: '',
  whatsAppNumber: '',
  presentStatus: '',
  domain: '',
  organization: '',
  designation: '',
  location: '',
  availability: '',
};

const TEXT_ONLY_FIELDS = new Set([
  'fullName',
  'collegeStudied',
  ...DROPDOWN_FIELDS.filter((f) => f !== 'gender' && f !== 'category'),
]);

const stripDigits = (value) => String(value || '').replace(/[0-9]/g, '');
const isTextOnlyValue = (value) => !/[0-9]/.test(String(value || ''));
const normalizeTrim = (value) => (value == null ? '' : String(value).trim());

const mergeUniqueOptions = (...lists) => {
  const map = new Map();
  lists.flat().forEach((item) => {
    const value = normalizeTrim(item);
    if (!value || value === OTHER_OPTION) return;
    const key = value.toLowerCase();
    if (!map.has(key)) map.set(key, value);
  });
  return Array.from(map.values()).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
};

const readLocalCustomOptions = () => {
  try {
    const raw = localStorage.getItem(LOCAL_OPTIONS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const writeLocalCustomOptions = (optionsMap) => {
  try {
    localStorage.setItem(LOCAL_OPTIONS_KEY, JSON.stringify(optionsMap));
  } catch {
    /* ignore */
  }
};

const steps = [
  { label: 'Profile Details', short: 'Profile Details' },
  { label: 'Finish', short: 'Finish' },
];

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: '#fff',
    overflow: 'hidden',
    '& fieldset': { borderColor: '#e0e0e0' },
    '&:hover fieldset': { borderColor: '#bdbdbd' },
    '&.Mui-focused fieldset': { borderColor: GREEN, borderWidth: 1.5 },
  },
  '& .MuiSelect-select': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
  },
  '& .MuiFormHelperText-root': {
    mx: 0.25,
    mt: 0.75,
  },
};

const selectMenuProps = {
  disableScrollLock: true,
  marginThreshold: 16,
  PaperProps: {
    sx: {
      maxHeight: 280,
      maxWidth: 'calc(100vw - 32px) !important',
      boxSizing: 'border-box',
      mt: 0.5,
      borderRadius: 2,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    },
  },
  MenuListProps: {
    sx: {
      py: 0.5,
      maxWidth: '100%',
      '& .MuiMenuItem-root': {
        fontSize: '0.9rem',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        minHeight: 40,
        py: 1,
        maxWidth: '100%',
      },
    },
  },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
};

const iconSx = { color: '#9e9e9e', fontSize: 20 };

const FieldLabel = ({ children, required, error }) => (
  <Typography
    variant="body2"
    sx={{
      mb: 0.75,
      fontWeight: 600,
      color: error ? 'error.main' : '#424242',
      fontSize: '0.875rem',
    }}
  >
    {children}
    {required ? (
      <Box component="span" sx={{ color: '#d32f2f', ml: 0.25 }}>
        *
      </Box>
    ) : null}
  </Typography>
);

FieldLabel.propTypes = {
  children: PropTypes.node.isRequired,
  required: PropTypes.bool,
  error: PropTypes.bool,
};

FieldLabel.defaultProps = {
  required: false,
  error: false,
};

const adornment = (Icon) => (
  <InputAdornment position="start">
    <Icon sx={iconSx} />
  </InputAdornment>
);

const DropdownWithOther = ({
  label,
  value,
  options,
  error,
  helperText,
  required,
  otherValue,
  onSelectChange,
  onOtherChange,
  onAddOption,
  adding,
  onKeyDown,
  icon: Icon,
  placeholder,
}) => {
  const showOther = value === OTHER_OPTION;
  const emptyLabel = placeholder || `Select ${label}`;

  return (
    <Stack spacing={1.25}>
      <Box sx={{ width: '100%', minWidth: 0 }}>
        <FieldLabel required={required} error={!!error}>
          {label}
        </FieldLabel>
        <TextField
          fullWidth
          select
          value={value || ''}
          onChange={onSelectChange}
          onKeyDown={onKeyDown}
          error={!!error}
          helperText={helperText || ' '}
          required={required}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: Icon ? adornment(Icon) : undefined,
          }}
          SelectProps={{
            displayEmpty: true,
            MenuProps: selectMenuProps,
            renderValue: (selected) => {
              if (!selected) {
                return (
                  <Typography component="span" sx={{ color: '#9e9e9e' }}>
                    {emptyLabel}
                  </Typography>
                );
              }
              if (selected === OTHER_OPTION) return 'Other (Add new option)';
              return selected;
            },
          }}
          sx={fieldSx}
        >
          <MenuItem value="" disabled>
            {emptyLabel}
          </MenuItem>
          {(options || []).map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
          <MenuItem value={OTHER_OPTION}>Other (Add new option)</MenuItem>
        </TextField>
      </Box>

      {showOther && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
          <TextField
            fullWidth
            label={`Add new ${label}`}
            value={otherValue || ''}
            onChange={onOtherChange}
            onKeyDown={onKeyDown}
            onKeyPress={(e) => {
              if (/[0-9]/.test(e.key)) e.preventDefault();
            }}
            error={!!error}
            helperText="Letters only — numbers are not allowed"
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: 200 }}
            placeholder={`Enter new ${String(label || '').toLowerCase()}`}
            sx={fieldSx}
          />
          <Button
            variant="contained"
            color="success"
            startIcon={adding ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            onClick={onAddOption}
            disabled={adding || !normalizeTrim(otherValue)}
            sx={{ minWidth: { sm: 140 }, height: 40, textTransform: 'none', fontWeight: 700 }}
          >
            Add Option
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

DropdownWithOther.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.string),
  error: PropTypes.string,
  helperText: PropTypes.string,
  required: PropTypes.bool,
  otherValue: PropTypes.string,
  onSelectChange: PropTypes.func.isRequired,
  onOtherChange: PropTypes.func.isRequired,
  onAddOption: PropTypes.func.isRequired,
  adding: PropTypes.bool,
  onKeyDown: PropTypes.func,
  icon: PropTypes.elementType,
  placeholder: PropTypes.string,
};

DropdownWithOther.defaultProps = {
  value: '',
  options: [],
  error: '',
  helperText: '',
  required: false,
  otherValue: '',
  adding: false,
  onKeyDown: undefined,
  icon: undefined,
  placeholder: '',
};

const UnknownAlumniRegistration = () => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const [customOptions, setCustomOptions] = useState(() => {
    const local = readLocalCustomOptions();
    const seeded = {};
    DROPDOWN_FIELDS.forEach((field) => {
      seeded[field] = mergeUniqueOptions(DEFAULT_OPTIONS[field] || [], local[field] || []);
    });
    return seeded;
  });
  const [otherInputs, setOtherInputs] = useState({});

  const batchToOptions = formData.batchFrom
    ? YEAR_OPTIONS.filter((year) => Number(year) >= Number(formData.batchFrom))
    : YEAR_OPTIONS;

  const getOptionsForField = useCallback(
    (field) => mergeUniqueOptions(DEFAULT_OPTIONS[field] || [], customOptions[field] || []),
    [customOptions]
  );

  useEffect(() => {
    const local = readLocalCustomOptions();
    const seeded = {};
    DROPDOWN_FIELDS.forEach((field) => {
      seeded[field] = mergeUniqueOptions(DEFAULT_OPTIONS[field] || [], local[field] || []);
    });
    setCustomOptions(seeded);
  }, []);

  const resetForm = useCallback(() => {
    setFormData({ ...initialForm, collegeStudied: 'KSRCT' });
    setOtherInputs({});
    setErrors({});
    setActiveStep(0);
  }, []);

  const getPayload = (resp) => resp?.data?.data ?? resp?.data ?? resp;

  const clearFieldError = (field) => {
    if (!errors[field]) return;
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const persistCustomOptionLocally = (field, value) => {
    const local = readLocalCustomOptions();
    local[field] = mergeUniqueOptions(local[field] || [], [value]);
    writeLocalCustomOptions(local);
    setCustomOptions((prev) => ({
      ...prev,
      [field]: mergeUniqueOptions(DEFAULT_OPTIONS[field] || [], prev[field] || [], [value]),
    }));
  };

  const handleAddDropdownOption = (field) => {
    const raw = stripDigits(otherInputs[field] || '');
    const value = normalizeTrim(raw);
    if (!value) {
      setErrors((prev) => ({ ...prev, [field]: 'Please enter a value to add' }));
      return;
    }
    if (value.length < 2) {
      setErrors((prev) => ({ ...prev, [field]: 'Option must be at least 2 characters' }));
      return;
    }
    if (!isTextOnlyValue(value)) {
      setErrors((prev) => ({ ...prev, [field]: 'Option cannot contain numbers' }));
      return;
    }

    persistCustomOptionLocally(field, value);
    setFormData((prev) => ({ ...prev, [field]: value }));
    setOtherInputs((prev) => ({ ...prev, [field]: '' }));
    clearFieldError(field);
    setNotification({
      open: true,
      message: `"${value}" added to dropdown options`,
      severity: 'success',
    });
  };

  const handleDropdownChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (value !== OTHER_OPTION) {
      setOtherInputs((prev) => ({ ...prev, [field]: '' }));
    }
    clearFieldError(field);
  };

  const handleOtherInputChange = (field) => (e) => {
    const value = stripDigits(e.target.value);
    setOtherInputs((prev) => ({ ...prev, [field]: value }));
    clearFieldError(field);
  };

  const resolveDropdownValue = (field) => {
    const selected = formData[field];
    if (selected === OTHER_OPTION) {
      return normalizeTrim(stripDigits(otherInputs[field] || ''));
    }
    return normalizeTrim(selected);
  };

  const handleTextChange = (field) => (e) => {
    let value = e.target.value;

    if (field === 'whatsAppNumber') {
      value = formatPhoneNumber(value);
    } else if (TEXT_ONLY_FIELDS.has(field)) {
      value = stripDigits(value);
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
    clearFieldError(field);
  };

  const handleBatchFromChange = (e) => {
    const batchFrom = e.target.value;
    setFormData((prev) => {
      const batchTo =
        prev.batchTo && Number(prev.batchTo) >= Number(batchFrom) ? prev.batchTo : '';
      const batch = batchFrom && batchTo ? `${batchFrom}-${batchTo}` : '';
      return { ...prev, batchFrom, batchTo, batch };
    });
    clearFieldError('batch');
    clearFieldError('batchFrom');
    clearFieldError('batchTo');
  };

  const handleBatchToChange = (e) => {
    const batchTo = e.target.value;
    setFormData((prev) => {
      const batch = prev.batchFrom && batchTo ? `${prev.batchFrom}-${batchTo}` : '';
      return { ...prev, batchTo, batch };
    });
    clearFieldError('batch');
    clearFieldError('batchTo');
  };

  const handlePhoneKeyPress = (e) => {
    const char = e.key;
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight',
      'ArrowUp', 'ArrowDown', 'Home', 'End',
    ];

    if (allowedKeys.includes(char)) {
      return;
    }

    const phoneCharRegex = /[\d\+\-\(\)\s]/;
    if (!phoneCharRegex.test(char)) {
      e.preventDefault();
    }
  };

  const handleTextOnlyKeyPress = (e) => {
    if (/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  };

  const validateEmail = (email) => {
    if (!email || !String(email).trim()) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const validatePhoneNumber = (phone) => {
    if (!phone || !String(phone).trim()) return false;
    const cleaned = phone.replace(/[\s\-\(\)]/g, '');
    const phoneRegex = /^\+?[0-9]{10,15}$/;
    return phoneRegex.test(cleaned);
  };

  const formatPhoneNumber = (value) => {
    return value.replace(/[^\d\+\-\(\)\s]/g, '');
  };

  const validateTextOnlyField = (value, label, { min = 2, max = 200 } = {}) => {
    const trimmed = normalizeTrim(value);
    if (!trimmed) return `${label} is required`;
    if (trimmed.length < min) return `${label} must be at least ${min} characters`;
    if (trimmed.length > max) return `${label} must be at most ${max} characters`;
    if (!isTextOnlyValue(trimmed)) return `${label} cannot contain numbers`;
    if (!/^[a-zA-Z\s\.\,\-\&\'\/\(\)]+$/.test(trimmed)) {
      return `${label} contains invalid characters`;
    }
    return '';
  };

  const validateDropdownField = (field, label) => {
    const selected = formData[field];
    if (!selected) return `${label} is required`;
    if (selected === OTHER_OPTION) {
      const custom = normalizeTrim(stripDigits(otherInputs[field] || ''));
      if (!custom) return `Please enter or add a custom ${label}`;
      return validateTextOnlyField(custom, label);
    }
    const options = getOptionsForField(field);
    if (!options.includes(selected)) return `Please select a valid ${label}`;
    return '';
  };

  const validateDateOfBirth = (date) => {
    if (!date) return { valid: false, message: 'Date of Birth is required' };
    const selectedDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate > today) {
      return { valid: false, message: 'Date of Birth cannot be a future date' };
    }

    const minDate = new Date();
    minDate.setFullYear(minDate.getFullYear() - 100);
    if (selectedDate < minDate) {
      return { valid: false, message: 'Date of Birth seems too old. Please verify.' };
    }

    const maxDob = new Date();
    maxDob.setFullYear(maxDob.getFullYear() - 16);
    if (selectedDate > maxDob) {
      return { valid: false, message: 'Please enter a valid Date of Birth' };
    }

    return { valid: true };
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      const nameError = validateTextOnlyField(formData.fullName, 'Full Name', { min: 2, max: 200 });
      if (nameError) newErrors.fullName = nameError;

      if (!formData.email || !String(formData.email).trim()) {
        newErrors.email = 'Email is required';
      } else if (!validateEmail(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }

      if (!formData.gender || !String(formData.gender).trim()) {
        newErrors.gender = 'Gender is required';
      } else {
        const genderError = validateDropdownField('gender', 'Gender');
        if (genderError) newErrors.gender = genderError;
      }

      const dateValidation = validateDateOfBirth(formData.dateOfBirth);
      if (!dateValidation.valid) {
        newErrors.dateOfBirth = dateValidation.message;
      }

      if (formData.rollNumber && formData.rollNumber.trim()) {
        if (!/^[a-zA-Z0-9\-\/]+$/.test(formData.rollNumber.trim())) {
          newErrors.rollNumber = 'Roll Number can only contain letters, numbers, - and /';
        } else if (formData.rollNumber.trim().length < 2) {
          newErrors.rollNumber = 'Roll Number must be at least 2 characters if provided';
        }
      }

      if (!formData.batchFrom) {
        newErrors.batchFrom = 'From year is required';
      }
      if (!formData.batchTo) {
        newErrors.batchTo = 'To year is required';
      } else if (formData.batchFrom && Number(formData.batchTo) < Number(formData.batchFrom)) {
        newErrors.batchTo = 'To year must be same as or after From year';
      }
      if (!formData.batchFrom || !formData.batchTo) {
        newErrors.batch = 'Select From and To years (e.g. 2020 – 2022)';
      }

      const collegeError = validateTextOnlyField(formData.collegeStudied, 'College Studied', {
        min: 3,
        max: 200,
      });
      if (collegeError) newErrors.collegeStudied = collegeError;

      const courseError = validateDropdownField('courseStudied', 'Course Studied');
      if (courseError) newErrors.courseStudied = courseError;

      const branchError = validateDropdownField('branch', 'Branch');
      if (branchError) newErrors.branch = branchError;

      const categoryError = validateDropdownField('category', 'Category');
      if (categoryError) newErrors.category = categoryError;

      if (!formData.whatsAppNumber || !String(formData.whatsAppNumber).trim()) {
        newErrors.whatsAppNumber = 'WhatsApp Number is required';
      } else if (!validatePhoneNumber(formData.whatsAppNumber)) {
        newErrors.whatsAppNumber = 'Please enter a valid phone number (10-15 digits, numbers only)';
      }
    }

    if (step === 1) {
      const presentStatusError = validateDropdownField('presentStatus', 'Present Status');
      if (presentStatusError) newErrors.presentStatus = presentStatusError;

      const domainError = validateDropdownField('domain', 'Domain');
      if (domainError) newErrors.domain = domainError;

      const orgError = validateDropdownField('organization', 'Organization');
      if (orgError) newErrors.organization = orgError;

      const desigError = validateDropdownField('designation', 'Designation');
      if (desigError) newErrors.designation = desigError;

      const locationError = validateDropdownField('location', 'Location');
      if (locationError) newErrors.location = locationError;

      const availabilityError = validateDropdownField('availability', 'Availability');
      if (availabilityError) newErrors.availability = availabilityError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const showNotification = useCallback((message, severity = 'info') => {
    setNotification({
      open: true,
      message,
      severity,
    });
  }, []);

  const handleCloseNotification = useCallback((event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    } else {
      showNotification('Please fix the errors before proceeding', 'error');
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (activeStep === 0) {
        handleNext();
      }
    }
  };

  const handleTextFieldKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (activeStep === 0) {
        handleNext();
      }
    }
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (activeStep !== steps.length - 1) {
      return;
    }

    if (!validateStep(activeStep)) {
      showNotification('Please fix the errors in the form', 'error');
      return;
    }

    setLoading(true);
    try {
      const resolvedFields = {};
      DROPDOWN_FIELDS.forEach((field) => {
        resolvedFields[field] = resolveDropdownValue(field);
        if (formData[field] === OTHER_OPTION && resolvedFields[field]) {
          persistCustomOptionLocally(field, resolvedFields[field]);
        }
      });

      const batch = formData.batch || `${formData.batchFrom}-${formData.batchTo}`;

      const payload = {
        fullName: formData.fullName,
        email: formData.email?.trim() || null,
        gender: resolvedFields.gender,
        dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth).toISOString().split('T')[0] : null,
        rollNumber: formData.rollNumber?.trim() || null,
        batch,
        collegeStudied: formData.collegeStudied?.trim() || 'KSRCT',
        courseStudied: resolvedFields.courseStudied,
        branch: resolvedFields.branch,
        category: resolvedFields.category,
        whatsAppNumber: formData.whatsAppNumber?.trim() || null,
        presentStatus: resolvedFields.presentStatus,
        domain: resolvedFields.domain,
        organization: resolvedFields.organization,
        designation: resolvedFields.designation,
        location: resolvedFields.location,
        availability: resolvedFields.availability,
      };

      const response = await api.post(API_ENDPOINTS.UNKNOWN_ALUMNI.REGISTER, payload);
      const data = getPayload(response);

      const isSuccess = data?.success ?? data?.Success ?? response?.data?.success ?? response?.data?.Success;

      if (isSuccess) {
        setSubmitted(true);
        showNotification('Registration submitted successfully! Your request will be reviewed by the administrator.', 'success');
        resetForm();
      } else {
        const errorMsg = data?.message || data?.Message || response?.data?.message || response?.data?.Message;
        if (errorMsg) {
          showNotification(errorMsg, 'error');
        } else {
          throw new Error('Failed to submit registration');
        }
      }
    } catch (error) {
      console.error('Registration error:', error);

      let errorMessage = null;

      if (error.response?.data) {
        const responseData = error.response.data;
        errorMessage =
          responseData.message ||
          responseData.Message ||
          responseData.error?.message ||
          responseData.error?.Message ||
          responseData.Error ||
          (typeof responseData === 'string' ? responseData : null);
      }

      if (!errorMessage) {
        errorMessage = error.message || 'Failed to submit registration. Please try again.';
      }

      showNotification(errorMessage, 'error');

      const lowerErrorMessage = errorMessage.toLowerCase();
      if (lowerErrorMessage.includes('email')) {
        setErrors((prev) => ({ ...prev, email: errorMessage }));
      } else if (lowerErrorMessage.includes('whatsapp')) {
        setErrors((prev) => ({ ...prev, whatsAppNumber: errorMessage }));
      } else if (lowerErrorMessage.includes('roll number')) {
        setErrors((prev) => ({ ...prev, rollNumber: errorMessage }));
      } else if (lowerErrorMessage.includes('mobile')) {
        setErrors((prev) => ({ ...prev, whatsAppNumber: errorMessage }));
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <PageLayout>
        <MDBox
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="80vh"
          px={3}
        >
          <Card
            sx={{
              p: 4,
              maxWidth: 600,
              textAlign: 'center',
              borderRadius: theme.borders.borderRadius.lg,
              boxShadow: theme.boxShadows.xl,
            }}
          >
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              width={100}
              height={100}
              borderRadius="50%"
              bgColor="success.main"
              mx="auto"
              mb={3}
            >
              <CheckCircleIcon sx={{ fontSize: 60, color: 'white' }} />
            </MDBox>
            <MDTypography variant="h4" fontWeight="bold" color="success" gutterBottom>
              Registration Submitted!
            </MDTypography>
            <MDTypography variant="body2" color="text" sx={{ mt: 2, mb: 3 }}>
              Thank you for your registration. Your request has been submitted successfully and will be reviewed by the administrator.
              You will be notified once your registration is approved.
            </MDTypography>
            <MDButton
              variant="gradient"
              color="success"
              onClick={() => setSubmitted(false)}
              sx={{ mt: 2 }}
            >
              Submit Another Registration
            </MDButton>
          </Card>
        </MDBox>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <MDBox
        display="flex"
        flexDirection="column"
        alignItems="center"
        minHeight="50vh"
        py={3}
        px={{ xs: 2, md: 3 }}
        sx={{ background: '#fafafa' }}
      >
        <Box sx={{ width: '100%', maxWidth: 960, minWidth: 0, overflowX: 'hidden' }}>
          {/* Brand header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1.5,
              mb: 3,
              pb: 2,
              borderBottom: '1px solid #eeeeee',
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: GREEN_SOFT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <SchoolIcon sx={{ color: GREEN, fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#212121', lineHeight: 1.2 }}>
                  KSRCT Alumni
                </Typography>
                <Typography variant="caption" sx={{ color: '#757575' }}>
                  Stay Connected · Build Together · Give Back
                </Typography>
              </Box>
            </Stack>
            <Typography
              variant="caption"
              sx={{ color: '#9e9e9e', fontStyle: 'italic', display: { xs: 'none', sm: 'block' } }}
            >
              Once a KSRCTian, Always a KSRCTian
            </Typography>
          </Box>

          {/* Title + step indicator */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#1b5e20',
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  mb: 0.5,
                }}
              >
                Join the Community
              </Typography>
              <Typography variant="body2" sx={{ color: '#757575' }}>
                Complete your profile to connect with fellow alumni.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pt: 0.5 }}>
              {steps.map((step, index) => {
                const active = index === activeStep;
                const done = index < activeStep;
                return (
                  <React.Fragment key={step.label}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.8rem',
                          fontWeight: 700,
                          bgcolor: active || done ? GREEN : '#e0e0e0',
                          color: active || done ? '#fff' : '#757575',
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: active ? 700 : 500,
                          color: active ? GREEN : '#9e9e9e',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {step.short}
                      </Typography>
                    </Stack>
                    {index < steps.length - 1 && (
                      <Box sx={{ width: 36, height: 2, bgcolor: done ? GREEN : '#e0e0e0' }} />
                    )}
                  </React.Fragment>
                );
              })}
            </Stack>
          </Box>

          <Card
            sx={{
              borderRadius: 3,
              boxShadow: '0 8px 28px rgba(0,0,0,0.06)',
              overflow: 'hidden',
              border: '1px solid #f0f0f0',
            }}
          >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (activeStep === steps.length - 1) {
                  handleSubmit(e);
                }
              }}
              onKeyDown={handleFormKeyDown}
            >
              <CardContent sx={{ minHeight: 270, py: 4, px: { xs: 2.5, md: 4 } }}>
                {activeStep === 0 && (
                  <Fade in={activeStep === 0} timeout={300}>
                    <Box>
                      <Grid container spacing={2.5} sx={{ '& > .MuiGrid-item': { minWidth: 0 } }}>
                        <Grid item xs={12} sm={6}>
                          <FieldLabel required error={!!errors.fullName}>
                            Full Name
                          </FieldLabel>
                          <TextField
                            fullWidth
                            value={formData.fullName}
                            onChange={handleTextChange('fullName')}
                            onKeyDown={handleTextFieldKeyDown}
                            onKeyPress={handleTextOnlyKeyPress}
                            error={!!errors.fullName}
                            helperText={errors.fullName || ' '}
                            required
                            inputProps={{ maxLength: 200 }}
                            placeholder="Enter your full name"
                            InputProps={{ startAdornment: adornment(PersonOutlineIcon) }}
                            sx={fieldSx}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FieldLabel required error={!!errors.email}>
                            Email
                          </FieldLabel>
                          <TextField
                            fullWidth
                            type="email"
                            value={formData.email}
                            onChange={handleTextChange('email')}
                            onKeyDown={handleTextFieldKeyDown}
                            error={!!errors.email}
                            helperText={errors.email || ' '}
                            required
                            inputProps={{ maxLength: 200 }}
                            placeholder="Enter your email address"
                            InputProps={{ startAdornment: adornment(EmailOutlinedIcon) }}
                            sx={fieldSx}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FieldLabel error={!!errors.rollNumber}>Roll Number</FieldLabel>
                          <TextField
                            fullWidth
                            value={formData.rollNumber}
                            onChange={handleTextChange('rollNumber')}
                            onKeyDown={handleTextFieldKeyDown}
                            error={!!errors.rollNumber}
                            helperText={errors.rollNumber || 'Used to identify your alumni record.'}
                            inputProps={{ maxLength: 50 }}
                            placeholder="Enter your roll number (optional)"
                            InputProps={{ startAdornment: adornment(BadgeOutlinedIcon) }}
                            sx={fieldSx}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <DropdownWithOther
                            label="Gender"
                            value={formData.gender}
                            options={getOptionsForField('gender')}
                            error={errors.gender}
                            helperText={errors.gender || ' '}
                            required
                            otherValue={otherInputs.gender}
                            onSelectChange={handleDropdownChange('gender')}
                            onOtherChange={handleOtherInputChange('gender')}
                            onAddOption={() => handleAddDropdownOption('gender')}
                            adding={false}
                            onKeyDown={handleTextFieldKeyDown}
                            icon={PersonOutlineIcon}
                            placeholder="Select gender"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FieldLabel required error={!!errors.dateOfBirth}>
                            Date of Birth
                          </FieldLabel>
                          <TextField
                            fullWidth
                            type="date"
                            value={formData.dateOfBirth}
                            onChange={handleTextChange('dateOfBirth')}
                            onKeyDown={handleTextFieldKeyDown}
                            error={!!errors.dateOfBirth}
                            helperText={errors.dateOfBirth || ' '}
                            required
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ startAdornment: adornment(CalendarMonthOutlinedIcon) }}
                            sx={fieldSx}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box
                            sx={{
                              border: '1px solid #e8e8e8',
                              borderRadius: 2.5,
                              p: 2,
                              bgcolor: '#fff',
                              width: '100%',
                              minWidth: 0,
                              overflow: 'hidden',
                            }}
                          >
                            <FieldLabel
                              required
                              error={!!errors.batch || !!errors.batchFrom || !!errors.batchTo}
                            >
                              Batch / Graduation Year
                            </FieldLabel>
                            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    mb: 0.5,
                                    color: errors.batchFrom ? 'error.main' : '#9e9e9e',
                                    fontWeight: 500,
                                  }}
                                >
                                  From{' '}
                                  <Box component="span" sx={{ color: '#d32f2f' }}>
                                    *
                                  </Box>
                                </Typography>
                                <TextField
                                  fullWidth
                                  select
                                  value={formData.batchFrom}
                                  onChange={handleBatchFromChange}
                                  onKeyDown={handleTextFieldKeyDown}
                                  error={!!errors.batchFrom || !!errors.batch}
                                  required
                                  InputLabelProps={{ shrink: true }}
                                  SelectProps={{
                                    displayEmpty: true,
                                    MenuProps: selectMenuProps,
                                    renderValue: (selected) => selected || 'From',
                                  }}
                                  sx={fieldSx}
                                >
                                  <MenuItem value="" disabled>
                                    From
                                  </MenuItem>
                                  {YEAR_OPTIONS.map((year) => (
                                    <MenuItem key={`from-${year}`} value={year}>
                                      {year}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Box>
                              <Typography
                                sx={{
                                  pt: 3.25,
                                  fontWeight: 600,
                                  color: '#9e9e9e',
                                  fontSize: '1.1rem',
                                  flexShrink: 0,
                                }}
                              >
                                —
                              </Typography>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    mb: 0.5,
                                    color: errors.batchTo ? 'error.main' : '#9e9e9e',
                                    fontWeight: 500,
                                  }}
                                >
                                  To{' '}
                                  <Box component="span" sx={{ color: '#d32f2f' }}>
                                    *
                                  </Box>
                                </Typography>
                                <TextField
                                  fullWidth
                                  select
                                  value={formData.batchTo}
                                  onChange={handleBatchToChange}
                                  onKeyDown={handleTextFieldKeyDown}
                                  error={!!errors.batchTo}
                                  required
                                  disabled={!formData.batchFrom}
                                  InputLabelProps={{ shrink: true }}
                                  SelectProps={{
                                    displayEmpty: true,
                                    MenuProps: selectMenuProps,
                                    renderValue: (selected) => selected || 'To',
                                  }}
                                  sx={fieldSx}
                                >
                                  <MenuItem value="" disabled>
                                    To
                                  </MenuItem>
                                  {batchToOptions.map((year) => (
                                    <MenuItem key={`to-${year}`} value={year}>
                                      {year}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Box>
                            </Stack>
                            {errors.batch && !formData.batchFrom && !formData.batchTo ? (
                              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                                {errors.batch}
                              </Typography>
                            ) : null}
                            {formData.batchFrom && formData.batchTo ? (
                              <Box
                                sx={{
                                  mt: 1.5,
                                  px: 1.5,
                                  py: 1,
                                  borderRadius: 1.5,
                                  bgcolor: GREEN_SOFT,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ color: GREEN, fontWeight: 600, fontSize: '0.85rem' }}
                                >
                                  Selected Batch: {formData.batchFrom} – {formData.batchTo}
                                </Typography>
                              </Box>
                            ) : null}
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FieldLabel required error={!!errors.collegeStudied}>
                            College Studied
                          </FieldLabel>
                          <TextField
                            fullWidth
                            value={formData.collegeStudied}
                            onChange={handleTextChange('collegeStudied')}
                            onKeyDown={handleTextFieldKeyDown}
                            onKeyPress={handleTextOnlyKeyPress}
                            error={!!errors.collegeStudied}
                            helperText={errors.collegeStudied || 'Default college is KSRCT'}
                            required
                            inputProps={{ maxLength: 200 }}
                            placeholder="KSRCT"
                            InputProps={{ startAdornment: adornment(BusinessOutlinedIcon) }}
                            sx={fieldSx}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <DropdownWithOther
                            label="Course Studied"
                            value={formData.courseStudied}
                            options={getOptionsForField('courseStudied')}
                            error={errors.courseStudied}
                            helperText={errors.courseStudied || ' '}
                            required
                            otherValue={otherInputs.courseStudied}
                            onSelectChange={handleDropdownChange('courseStudied')}
                            onOtherChange={handleOtherInputChange('courseStudied')}
                            onAddOption={() => handleAddDropdownOption('courseStudied')}
                            adding={false}
                            onKeyDown={handleTextFieldKeyDown}
                            icon={MenuBookOutlinedIcon}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <DropdownWithOther
                            label="Branch"
                            value={formData.branch}
                            options={getOptionsForField('branch')}
                            error={errors.branch}
                            helperText={errors.branch || ' '}
                            required
                            otherValue={otherInputs.branch}
                            onSelectChange={handleDropdownChange('branch')}
                            onOtherChange={handleOtherInputChange('branch')}
                            onAddOption={() => handleAddDropdownOption('branch')}
                            adding={false}
                            onKeyDown={handleTextFieldKeyDown}
                            icon={AccountTreeOutlinedIcon}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <DropdownWithOther
                            label="Category"
                            value={formData.category}
                            options={getOptionsForField('category')}
                            error={errors.category}
                            helperText={errors.category || ' '}
                            required
                            otherValue={otherInputs.category}
                            onSelectChange={handleDropdownChange('category')}
                            onOtherChange={handleOtherInputChange('category')}
                            onAddOption={() => handleAddDropdownOption('category')}
                            adding={false}
                            onKeyDown={handleTextFieldKeyDown}
                            icon={LocalOfferOutlinedIcon}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FieldLabel required error={!!errors.whatsAppNumber}>
                            WhatsApp Number
                          </FieldLabel>
                          <TextField
                            fullWidth
                            value={formData.whatsAppNumber}
                            onChange={handleTextChange('whatsAppNumber')}
                            onKeyDown={handleTextFieldKeyDown}
                            onKeyPress={handlePhoneKeyPress}
                            error={!!errors.whatsAppNumber}
                            helperText={errors.whatsAppNumber || 'Enter numbers only (e.g., +91 9876543210)'}
                            required
                            inputProps={{ maxLength: 20 }}
                            placeholder="Enter WhatsApp number"
                            InputProps={{ startAdornment: adornment(WhatsAppIcon) }}
                            sx={fieldSx}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Fade>
                )}

                {activeStep === 1 && (
                  <Fade in={activeStep === 1} timeout={300}>
                    <Box>
                      <Grid container spacing={2.5} sx={{ '& > .MuiGrid-item': { minWidth: 0 } }}>
                        <Grid item xs={12} sm={6}>
                          <DropdownWithOther
                            label="Present Status"
                            value={formData.presentStatus}
                            options={getOptionsForField('presentStatus')}
                            error={errors.presentStatus}
                            helperText={errors.presentStatus || ' '}
                            required
                            otherValue={otherInputs.presentStatus}
                            onSelectChange={handleDropdownChange('presentStatus')}
                            onOtherChange={handleOtherInputChange('presentStatus')}
                            onAddOption={() => handleAddDropdownOption('presentStatus')}
                            adding={false}
                            onKeyDown={handleTextFieldKeyDown}
                            icon={WorkIcon}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <DropdownWithOther
                            label="Domain"
                            value={formData.domain}
                            options={getOptionsForField('domain')}
                            error={errors.domain}
                            helperText={errors.domain || ' '}
                            required
                            otherValue={otherInputs.domain}
                            onSelectChange={handleDropdownChange('domain')}
                            onOtherChange={handleOtherInputChange('domain')}
                            onAddOption={() => handleAddDropdownOption('domain')}
                            adding={false}
                            onKeyDown={handleTextFieldKeyDown}
                            icon={DomainOutlinedIcon}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <DropdownWithOther
                            label="Organization"
                            value={formData.organization}
                            options={getOptionsForField('organization')}
                            error={errors.organization}
                            helperText={errors.organization || ' '}
                            required
                            otherValue={otherInputs.organization}
                            onSelectChange={handleDropdownChange('organization')}
                            onOtherChange={handleOtherInputChange('organization')}
                            onAddOption={() => handleAddDropdownOption('organization')}
                            adding={false}
                            onKeyDown={handleTextFieldKeyDown}
                            icon={BusinessOutlinedIcon}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <DropdownWithOther
                            label="Designation"
                            value={formData.designation}
                            options={getOptionsForField('designation')}
                            error={errors.designation}
                            helperText={errors.designation || ' '}
                            required
                            otherValue={otherInputs.designation}
                            onSelectChange={handleDropdownChange('designation')}
                            onOtherChange={handleOtherInputChange('designation')}
                            onAddOption={() => handleAddDropdownOption('designation')}
                            adding={false}
                            onKeyDown={handleTextFieldKeyDown}
                            icon={BadgeOutlinedIcon}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <DropdownWithOther
                            label="Location"
                            value={formData.location}
                            options={getOptionsForField('location')}
                            error={errors.location}
                            helperText={errors.location || ' '}
                            required
                            otherValue={otherInputs.location}
                            onSelectChange={handleDropdownChange('location')}
                            onOtherChange={handleOtherInputChange('location')}
                            onAddOption={() => handleAddDropdownOption('location')}
                            adding={false}
                            onKeyDown={handleTextFieldKeyDown}
                            icon={PlaceOutlinedIcon}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <DropdownWithOther
                            label="Availability"
                            value={formData.availability}
                            options={getOptionsForField('availability')}
                            error={errors.availability}
                            helperText={errors.availability || ' '}
                            required
                            otherValue={otherInputs.availability}
                            onSelectChange={handleDropdownChange('availability')}
                            onOtherChange={handleOtherInputChange('availability')}
                            onAddOption={() => handleAddDropdownOption('availability')}
                            adding={false}
                            onKeyDown={handleTextFieldKeyDown}
                            icon={PublicOutlinedIcon}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Fade>
                )}
              </CardContent>

              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={3}
                sx={{
                  borderTop: '1px solid #eeeeee',
                  backgroundColor: '#fafafa',
                }}
              >
                <Box>
                  {activeStep > 0 && (
                    <MDButton
                      type="button"
                      variant="outlined"
                      color="dark"
                      onClick={handleBack}
                      disabled={loading}
                      startIcon={<ChevronLeftIcon />}
                    >
                      Back
                    </MDButton>
                  )}
                </Box>
                <Box>
                  {activeStep < steps.length - 1 ? (
                    <Button
                      type="button"
                      variant="contained"
                      onClick={handleNext}
                      endIcon={<ChevronRightIcon />}
                      sx={{
                        bgcolor: GREEN,
                        color: '#fff',
                        px: 3,
                        py: 1.25,
                        borderRadius: 2,
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        boxShadow: '0 4px 14px rgba(46,125,50,0.35)',
                        '&:hover': { bgcolor: '#1b5e20' },
                      }}
                    >
                      Next Step
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="contained"
                      disabled={loading}
                      onClick={handleSubmit}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                      sx={{
                        bgcolor: GREEN,
                        color: '#fff',
                        px: 3,
                        py: 1.25,
                        borderRadius: 2,
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        boxShadow: '0 4px 14px rgba(46,125,50,0.35)',
                        '&:hover': { bgcolor: '#1b5e20' },
                      }}
                    >
                      {loading ? 'Processing...' : 'Complete Registration'}
                    </Button>
                  )}
                </Box>
              </MDBox>
            </form>
          </Card>
        </Box>
      </MDBox>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
          elevation={6}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default UnknownAlumniRegistration;
